import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { POST_SELF_EDIT_WINDOW_LABEL, POST_SELF_EDIT_WINDOW_MS } from '@/lib/post-edit-window';
import { useNavigate } from 'react-router-dom';
import { ProfileLink } from '@/components/profile/ProfileLink';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Heart,
  MessageSquare,
  Share2,
  MoreHorizontal,
  Flag,
  Trash2,
  Eye,
  Bookmark,
  Pencil,
  ShieldCheck,
} from 'lucide-react';
import { useUser } from '@/hooks/use-user';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SocialPost } from '../types';
import { PostCommentsDialog } from './PostCommentsDialog';
import { ReportDialog } from './ReportDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

import { GeneralPostContent } from './content/GeneralPostContent';
import { LeadPostContent } from './content/LeadPostContent';
import { TestimonialPostContent } from './content/TestimonialPostContent';
import { ThankYouPostContent } from './content/ThankYouPostContent';
import { MeetupPostContent } from './content/MeetupPostContent';
import { PollPostContent } from './content/PollPostContent';

interface PostCardProps {
  post: SocialPost;
  onDelete?: (id: string) => void;
  isLeadDashboard?: boolean;
  communityId?: string;
  isUnread?: boolean;
  onUnreadViewed?: (postId: string) => void;
}

// Helper to get author name from flat fields
const getAuthorName = (post: SocialPost) => {
  const firstName = post.authorFirstName || '';
  const lastName = post.authorLastName || '';
  return `${firstName} ${lastName}`.trim() || 'Unknown User';
};

const getPostSummary = (post: SocialPost) => {
  switch (post.type) {
    case 'GENERAL':
      return post.generalContent;
    case 'LEAD':
      return post.leadTitle;
    case 'TESTIMONIAL':
      return post.testimonialContent;
    case 'THANK_YOU':
      return post.thankYouMessage;
    case 'MEETUP':
      return `Meetup on ${new Date(post.meetupDate).toLocaleDateString()}`;
    case 'POLL':
      return post.pollQuestion;
    default:
      return '';
  }
};

const COMMUNITY_ADMIN_ROLES = ['ADMIN', 'CREATOR', 'OWNER'];
const EDITED_TOLERANCE_MS = 1000;

const postTypeDisplay: Record<SocialPost['type'], string> = {
  GENERAL: 'General',
  LEAD: 'Lead',
  TESTIMONIAL: 'Testimonial',
  THANK_YOU: 'Thank You Note',
  MEETUP: 'Meetup',
  POLL: 'Poll',
};

export function PostCard({
  post,
  onDelete,
  isLeadDashboard = false,
  communityId,
  isUnread = false,
  onUnreadViewed,
}: PostCardProps) {
  const { user } = useUser();
  const navigate = useNavigate();
  const authorName = getAuthorName(post);
  const isAuthor = user?.id === post.authorId;
  const isCommunityAdmin =
    user?.communities
      .find((community) => community.id === post.communityId)
      ?.roles.some((role) => COMMUNITY_ADMIN_ROLES.includes(role.toUpperCase())) ?? false;
  const isWithinSelfDeleteWindow =
    Date.now() - new Date(post.createdAt).getTime() <= POST_SELF_EDIT_WINDOW_MS;
  const isSelfDeleteExpired = isAuthor && !isWithinSelfDeleteWindow;
  const showAdminDelete = isCommunityAdmin && !(isAuthor && isWithinSelfDeleteWindow);
  const isEditable =
    isAuthor &&
    (post.type === 'THANK_YOU' ||
      post.type === 'LEAD' ||
      post.type === 'TESTIMONIAL' ||
      post.type === 'MEETUP');
  const isEditExpired = isEditable && !isWithinSelfDeleteWindow;
  const isEdited =
    !!post.contentUpdatedAt &&
    new Date(post.contentUpdatedAt).getTime() - new Date(post.createdAt).getTime() >
      EDITED_TOLERANCE_MS;
  const [showUnreadGlow, setShowUnreadGlow] = React.useState(isUnread);
  const onUnreadViewedRef = React.useRef(onUnreadViewed);
  onUnreadViewedRef.current = onUnreadViewed;

  React.useEffect(() => {
    setShowUnreadGlow(isUnread);
  }, [isUnread, post.id]);

  const timeAgo = formatDistanceToNow(new Date(post.createdAt), {
    addSuffix: true,
  });

  /* Interactive State */
  const [isLiked, setIsLiked] = React.useState(post.isLiked);
  const [likeCount, setLikeCount] = React.useState(Number(post.likeCount) || 0);
  const [isSharing, setIsSharing] = React.useState(false);
  const [commentCount, setCommentCount] = React.useState(Number(post.commentCount) || 0);
  const [viewCount, setViewCount] = React.useState(Number(post.viewCount) || 0);
  const [showComments, setShowComments] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [expiredNotice, setExpiredNotice] = React.useState<'delete' | 'edit' | null>(null);
  const [showReportDialog, setShowReportDialog] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isBookmarked, setIsBookmarked] = React.useState(!!post.isBookmarked);
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsLiked(post.isLiked);
    setLikeCount(Number(post.likeCount) || 0);
    setCommentCount(Number(post.commentCount) || 0);
    setViewCount(Number(post.viewCount) || 0);
    setIsBookmarked(!!post.isBookmarked);
  }, [post.isLiked, post.likeCount, post.commentCount, post.viewCount, post.isBookmarked]);

  const markAsSeen = React.useCallback(() => {
    setShowUnreadGlow(false);
    onUnreadViewedRef.current?.(post.id);
    void axiosInstance
      .post(`/api/posts/${post.id}/view`)
      .then((res) => {
        if (res.data.success && res.data.recorded) {
          setViewCount((prev) => prev + 1);
        }
      })
      .catch(console.error);
  }, [post.id]);

  const renderContent = () => {
    switch (post.type) {
      case 'GENERAL':
        return <GeneralPostContent post={post} />;
      case 'LEAD':
        return <LeadPostContent post={post} user={user} />;
      case 'TESTIMONIAL':
        return <TestimonialPostContent post={post} />;
      case 'THANK_YOU':
        return <ThankYouPostContent post={post} />;
      case 'MEETUP':
        return <MeetupPostContent post={post} />;
      case 'POLL':
        return <PollPostContent post={post} onInteracted={markAsSeen} />;
      default: {
        const _exhaustiveCheck: never = post;
        return null;
      }
    }
  };
  React.useEffect(() => {
    if (!cardRef.current || !post.id) return;

    const VIEW_DWELL_MS = 5000;
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    let hasRecorded = false;

    const clearDwell = () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const recordView = () => {
      if (hasRecorded) return;
      hasRecorded = true;
      markAsSeen();
      observer.disconnect();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || hasRecorded) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          clearDwell();
          dwellTimer = setTimeout(recordView, VIEW_DWELL_MS);
        } else {
          clearDwell();
        }
      },
      { threshold: [0, 0.5, 0.75] }
    );

    observer.observe(cardRef.current);

    return () => {
      clearDwell();
      observer.disconnect();
    };
  }, [post.id, markAsSeen]);

  React.useEffect(() => {
    if (!isMenuOpen) return;

    const handleScroll = () => {
      setIsMenuOpen(false);
    };

    window.addEventListener('scroll', handleScroll, { capture: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isMenuOpen]);
  const handleLike = async () => {
    const previousLiked = isLiked;
    const previousCount = likeCount;

    setIsLiked(!previousLiked);
    setLikeCount(previousLiked ? previousCount - 1 : previousCount + 1);
    markAsSeen();

    try {
      await axiosInstance.post(`/api/posts/${post.id}/like`);
    } catch (_error) {
      setIsLiked(previousLiked);
      setLikeCount(previousCount);
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    setIsSharing(true);
    const shareCommunityId = communityId || post.communityId;
    const shareUrl = shareCommunityId
      ? `${window.location.origin}/${shareCommunityId}/posts/${post.id}`
      : `${window.location.origin}/posts/${post.id}`;
    const summary = getPostSummary(post);
    const shareData = {
      title: `Post by ${authorName}`,
      text: summary || 'Check out this post on Milora',
      url: shareUrl,
    };

    try {
      await axiosInstance.post(`/api/posts/${post.id}/share`, { platform: 'internal' });
      markAsSeen();

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
      if (error instanceof Error && error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeletePost = async () => {
    try {
      await axiosInstance.delete(`/api/posts/${post.id}`);
      toast.success('Removed from feed');
      if (onDelete) {
        onDelete(post.id);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Delete post error:', error);
      toast.error('Failed to delete post');
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteAllPosts = async () => {
    try {
      await axiosInstance.delete(`/api/posts/${post.id}?type=all`);
      toast.success(`${postTypeDisplay[post.type]} deleted`);
      if (onDelete) {
        onDelete(post.id);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Delete all posts error:', error);
      toast.error(`Failed to delete post and the ${postTypeDisplay[post.type]} `);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleBookmark = async () => {
    setIsBookmarked((prev) => !prev);
    markAsSeen();

    try {
      await axiosInstance.post(`/api/posts/${post.id}/bookmark`);
    } catch (_error) {
      setIsBookmarked((prev) => !prev);
      toast.error('Failed to update bookmark');
    }
  };
  return (
    <>
      <Card
        ref={cardRef}
        className={cn(
          'overflow-hidden border-2 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ease-out relative',
          showUnreadGlow
            ? 'border-primary/45 shadow-primary/15 ring-1 ring-primary/25'
            : 'border-black/15 dark:border-white/15 shadow-black/15 dark:shadow-gray-400/15 hover:shadow-black/25 dark:hover:shadow-gray-300/25 hover:border-black/25 dark:hover:border-white/25'
        )}
      >
        <CardHeader className="flex flex-row items-start gap-4 p-4 pb-2">
          <ProfileLink
            userId={post.authorId}
            name={authorName}
            image={post.authorImage || undefined}
            showName={false}
            className="shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <ProfileLink
                  userId={post.authorId}
                  showAvatar={false}
                  className="text-sm font-semibold truncate hover:underline cursor-pointer"
                >
                  {authorName}
                </ProfileLink>
                <p className="text-xs text-muted-foreground">
                  {post.authorJobTitle ? `${post.authorJobTitle} • ` : ''}
                  {timeAgo}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {showUnreadGlow && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
                    </span>
                    New
                  </span>
                )}
                {!isLeadDashboard && (
                  <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {user?.id !== post.authorId && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowReportDialog(true);
                          }}
                          className="text-red-500 hover:text-red-600 focus:text-red-600"
                        >
                          <Flag className="mr-2 h-4 w-4" />
                          <span>{post.type === 'POLL' ? 'Report poll' : 'Report'}</span>
                        </DropdownMenuItem>
                      )}
                      {isEditable && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsMenuOpen(false);
                            if (isEditExpired) {
                              setExpiredNotice('edit');
                              return;
                            }
                            navigate(`/${post.communityId}/posts/${post.id}/edit`);
                          }}
                          className={cn(
                            isEditExpired
                              ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                              : 'text-foreground'
                          )}
                          aria-disabled={isEditExpired}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                      )}
                      {isAuthor && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsMenuOpen(false);
                            if (isSelfDeleteExpired) {
                              setExpiredNotice('delete');
                              return;
                            }
                            setShowDeleteDialog(true);
                          }}
                          className={cn(
                            isSelfDeleteExpired
                              ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                              : 'text-red-500 hover:text-red-600 focus:text-red-600'
                          )}
                          aria-disabled={isSelfDeleteExpired}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      )}
                      {showAdminDelete && (
                        <DropdownMenuItem
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-500 hover:text-red-600 focus:text-red-600"
                        >
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          <span>Delete as Admin</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-2">{renderContent()}</CardContent>
        {isEdited && (
          <div className="flex justify-end px-4 pb-2">
            <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Edited
            </span>
          </div>
        )}
        {!isLeadDashboard && post.type !== 'POLL' && (
          <CardFooter className="p-2 border-t bg-muted/20 flex justify-between items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLike}
              className="flex-1 gap-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
              <span className="text-xs">{likeCount}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(true)}
              className="flex-1 gap-2 text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10 transition-colors"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="text-xs">{commentCount}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 gap-2 text-muted-foreground pointer-events-none transition-colors"
            >
              <Eye className="h-4 w-4" />
              <span className="text-xs">
                {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(viewCount)}
              </span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 gap-2 text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleBookmark}
            >
              <Bookmark
                className={`h-4 w-4 red-500 ${isBookmarked ? 'fill-red-500 text-red-500' : ''}`}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              disabled={isSharing}
              className="flex-1 gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </CardFooter>
        )}
      </Card>
      <PostCommentsDialog
        postId={post.id}
        open={showComments}
        onOpenChange={setShowComments}
        onCommentAdded={() => {
          setCommentCount((prev) => Number(prev) + 1);
          markAsSeen();
        }}
        communityId={communityId}
      />
      <ReportDialog
        postId={post.id}
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        entityLabel={post.type === 'POLL' ? 'poll' : 'post'}
        onReported={() => {
          if (onDelete) {
            onDelete(post.id);
          } else {
            window.location.reload();
          }
        }}
      />

      <AlertDialog
        open={expiredNotice !== null}
        onOpenChange={(open) => !open && setExpiredNotice(null)}
      >
        <AlertDialogContent className="w-[calc(100%-2rem)] sm:w-full rounded-2xl">
          <AlertDialogHeader className="text-center sm:text-center">
            <AlertDialogTitle>
              {expiredNotice === 'edit'
                ? 'Edit is no longer available'
                : 'Delete is no longer available'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {expiredNotice === 'edit'
                ? `Posts can only be edited by their author within ${POST_SELF_EDIT_WINDOW_LABEL} of posting. Please contact your admin to make changes.`
                : `Posts can only be deleted by their author within ${POST_SELF_EDIT_WINDOW_LABEL} of posting. Please contact your admin to remove this post.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="w-[calc(100%-2rem)] sm:w-full rounded-2xl">
          <AlertDialogHeader className="text-center sm:text-center">
            <AlertDialogTitle>
              {post.type === 'POLL' ? 'Delete this poll?' : 'Remove this from the feed?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {post.type === 'POLL'
                ? 'This will permanently delete the poll and all votes. This action cannot be undone.'
                : 'You can remove just this feed post, or delete the underlying content as well.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col !space-x-0 !space-y-2 sm:flex-col sm:!space-x-0 sm:!space-y-2">
            {post.type === 'POLL' ? (
              <AlertDialogAction
                className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
                onClick={handleDeleteAllPosts}
              >
                Delete Poll
              </AlertDialogAction>
            ) : (
              <>
                <AlertDialogAction
                  className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
                  onClick={handleDeletePost}
                >
                  Remove from feed
                </AlertDialogAction>
                {post.type !== 'GENERAL' && (
                  <AlertDialogAction
                    className="w-full bg-red-600 hover:bg-red-700 text-white border-none"
                    onClick={handleDeleteAllPosts}
                  >
                    Delete {postTypeDisplay[post.type]} and all feed posts
                  </AlertDialogAction>
                )}
              </>
            )}
            <AlertDialogCancel className="w-full mt-2">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
